/**
 * This file contains the models for the external webviewer schemas that are sent to the external webviewer iframe.
 * These schemas are specified in the webviewer application source code and define the interface contract.
 *
 * See https://ca-digit.gitlab.io/web-viewer/schema/viewer-data-container.schema.json for the current schema.
 */

/**
 * The base interface for all webviewer data containers.
 */
export type WebViewerDataContainer = {
	version: string;
	type: WebViewerDataContainerType;
	data: WebViewerData;
	meta: WebViewerDataContainerMeta;
};

/**
 * The type of the webviewer data container.
 */
export enum WebViewerDataContainerType {
	MODIFICATION = "modification",
	VIEWER = "viewer",
	ALIGNER = "aligner",
	BRACKETS = "brackets",
	RETAINER = "retainer",
}

/**
 * The meta data for the webviewer data container.
 */
export type WebViewerDataContainerMeta = {
	createdBy?: string;
	createdAt?: string;
	createdByRole?: WebviewerCreatedByRole;
	entityId?: string;
	mediaId?: string;
	referenceId?: string;
	treatmentId?: string;
	target?: Omit<WebViewerDataContainerMeta, "target">;
};

/**
 * The data for the webviewer data container.
 */
export type WebViewerData = {
	models: any[];
	modification?: WebViewerModification;
};

/**
 * The data for the webviewer data container for the 'modification' file type.
 */
export type WebViewerModificationData = WebViewerData & {
	modification: WebViewerModification;
};

/**
 * A single marker in the webviewer modification data.
 */
export type WebViewerMarker = {
	comment: string;
	name: string;
	position: WebViewerVector3;
	timestamp?: string;
	createdByRole?: WebviewerCreatedByRole;
	markedObject: WebViewerMarkedObject;
};

/**
 * The modification data container.
 */
export type WebViewerModification = {
	markers: WebViewerMarker[];
};

/**
 * The interface for the webviewer data container for the 'modification' file type.
 */
export type WebViewerModificationDataContainer = WebViewerDataContainer & {
	type: WebViewerDataContainerType.MODIFICATION;
	data: WebViewerModificationData;
};

/**
 * A marked object in the webviewer.
 */
export type WebViewerMarkedObject = {
	name: string;
	meta: WebViewerModelMeta;
};

/**
 * The role of the user who created the webviewer data container or modification.
 */
export enum WebviewerCreatedByRole {
	TECHNICIAN = "tec",
	DOCTOR = "doc",
}

/**
 * Jaw position enum for marked object meta.
 */
export enum WebViewerJaw {
	UPPER = "upper",
	LOWER = "lower",
}


/**
 * A quaternion represented as [x, y, z, w].
 */
export type WebViewerQuaternion = [number, number, number, number];


/**
 * A 3D vector represented as [x, y, z].
 */
export type WebViewerVector3 = [number, number, number];

/**
 * A transformation represented by rotation and translation.
 */
export type WebViewerTransform = {
	/**
	 * A quaternion representing rotation [x, y, z, w].
	 */
	rotation: WebViewerQuaternion;
	/**
	 * Translation vector [x, y, z].
	 */
	translation: WebViewerVector3;
	/**
	 * Optional scale vector [x, y, z].
	 */
	scale?: WebViewerVector3;
};

/**
 * Meta data for a marked object in the webviewer.
 * This represents metadata associated with objects that can be marked in the 3D viewer.
 */
export type WebViewerModelMeta = {
	/**
	 * Jaw position (upper or lower).
	 */
	jaw?: WebViewerJaw;
	/**
	 * Step number (minimum: -1).
	 */
	step?: number;
	/**
	 * FDI notation number for tooth identification (range: 11-48).
	 */
	fdi?: number;
	/**
	 * Unique identifier for the model. This identifier should be unique across all models
	 * and can be used for referencing from other parts of the file.
	 */
	id?: string;
	/**
	 * Applied world transformation of the model. An inversion of this transform moves
	 * the model to its untransformed origin state.
	 */
	transform?: WebViewerTransform;
};
